import type { Blueprint } from '#layers/studio/app/runtime/builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '#layers/studio/app/runtime/builder/base/blueprints/ValidatableBlueprint';
import type { ValidationErrors } from '#layers/studio/app/runtime/builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '#layers/studio/app/runtime/builder/form';
import type { BlueprintDefinition } from '#layers/studio/app/runtime/builder/form/blueprints/BlueprintDefinition';
import type { VisibleBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/VisibleBlueprint';
import { AlwaysChoice } from '#layers/studio/app/runtime/builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '#layers/studio/app/runtime/builder/form/enums/InternallyChoice';
import type { NeverChoice } from '#layers/studio/app/runtime/builder/form/enums/NeverChoice';
import type { WhenChoice } from '#layers/studio/app/runtime/builder/form/enums/WhenChoice';
import type { Entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { ValidEntry } from '#layers/studio/app/runtime/builder/form/entries/ValidEntry';
import type { EntryFactory } from '#layers/studio/app/runtime/builder/form/traits/EntryFactory';
import type { HasWidth } from '#layers/studio/app/runtime/builder/base/traits/HasWidth';
import type { HasHelp } from '#layers/studio/app/runtime/builder/form/traits/HasHelp';
import type { HasLabel } from '#layers/studio/app/runtime/builder/form/traits/HasLabel';
import type { ProcessCallback } from '#layers/studio/app/runtime/builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'comments',
    name: '[[[Komentarze]]]',
    icon: 'fa-comment',
    group: 'cms',
    position: 14
};

export class CommentsEntry extends ValidEntry<string>
{
    public data: any;
    public type: string = Definition.type;

    public constructor(data: any = null)
    {
        super();
    }

    public async collect(blueprint: CommentsContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        await preprocess(blueprint, this, form.blueprintId, form.entryId);

        return entry({
            type: this.type
        });
    }

    public validate(blueprint: CommentsContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        return this.valid();
    }
}

export const instanceOfCommentsEntry = (object: any): object is CommentsEntry =>
{
    return object && 'type' in object && object.type === Definition.type;
};

export interface CommentsContract extends Blueprint, VisibleBlueprint, HasWidth, HasLabel, HasHelp
{
    authOnlyCanRead: boolean;
    authOnlyCanWrite: boolean;
    authOnlyCanLike: boolean;
    usersCanReport: boolean;
}

export class CommentsType implements CommentsContract, ValidatableBlueprint, EntryFactory<CommentsEntry>, HasHelp
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public width: number;
    public minWidth: number;
    public errors: ValidationErrors;
    public help: Record<string, string>;
    public authOnlyCanRead: boolean;
    public authOnlyCanWrite: boolean;
    public authOnlyCanLike: boolean;
    public usersCanReport: boolean;

    public constructor(id: string, name: string, width: number)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Komentarze' };
        this.showLabel = true;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.width = Math.min(6, Math.max(this.minWidth, width));
        this.minWidth = 6;
        this.errors = {};
        this.help = {};
        this.authOnlyCanRead = false;
        this.authOnlyCanWrite = false;
        this.authOnlyCanLike = false;
        this.usersCanReport = false;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(6, Math.max(this.minWidth, width));
    }

    public createEntry(): CommentsEntry
    {
        return new CommentsEntry();
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }
}
